'use client';
import { useRef, useState } from 'react';
import { SearchGame } from '@/features/search-game';
import { SelectCategory } from '@/features/select-category';
import { SelectProvider } from '@/features/select-provider';
import {
  Wrapper,
  SearchWrapper,
  SelectsWrapper,
  DeleteFilterWrapper,
} from './styles';
import { DeleteFilter } from '@/features/delete-filter';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { Condition } from '@/shared/ui/condition';
import { Button } from '@/shared/ui/form/button/button';
import { Image } from '@/shared/ui/image';
import { Box } from '@/shared/ui/box';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';

type DefaultLayoutProps = {
  isMobileMenu: boolean;
  mobileLandscape: boolean;
};

const FilterConfig = {
  tablet: {
    render: (props) => <MobileLandscapeLayout {...props} />,
  },
  default: {
    render: (props) => <DefaultLayout {...props} />,
  },
};

const MobileLandscapeLayout = ({
  isMobileMenu,
  searchFocused,
  setSearchFocused,
}) => {
  return (
    <>
      <Wrapper $alignToLeft={isMobileMenu}>
        <SelectsWrapper>
          {!searchFocused ? (
            isMobileMenu ? (
              <MobileSearchOnly
                setSearchFocused={setSearchFocused}
                searchFocused={searchFocused}
              />
            ) : (
              <DesktopControls setSearchFocused={setSearchFocused} />
            )
          ) : (
            <SearchWithControls
              isMobileMenu={isMobileMenu}
              searchFocused={searchFocused}
              setSearchFocused={setSearchFocused}
            />
          )}
        </SelectsWrapper>
      </Wrapper>
      {!isMobileMenu && (
        <DeleteFilterWrapper>
          <DeleteFilter />
        </DeleteFilterWrapper>
      )}
    </>
  );
};

const MobileSearchOnly = ({ setSearchFocused, searchFocused }) => (
  <Box $gap={8} $display="flex" $fill>
    <Box $fill>
      <SearchGame
        setSearchFocused={setSearchFocused}
        searchFocused={searchFocused}
        mobileLandscape={true}
      />
    </Box>
  </Box>
);

const DesktopControls = ({ setSearchFocused }) => (
  <>
    <Box
      $gap={8}
      $display="grid"
      style={{ gridTemplateColumns: 'repeat(2, 62.5px)', gap: 8 }}
    >
      <Box onClick={() => setSearchFocused(true)} $flexBasis="50%">
        <Button
          style={{ height: '42px', width: '100%' }}
          icon={
            <Image
              width={24}
              height={24}
              src="/images/icons/search-icon-white.svg"
            />
          }
          variant="gray"
        />
      </Box>
      <Box>
        <SelectCategory />
      </Box>
    </Box>
    <Box $width="100%">
      <SelectProvider searchFocused={false} />
    </Box>
  </>
);

const SearchWithControls = ({
  isMobileMenu,
  searchFocused,
  setSearchFocused,
}) => (
  <Box $gap={8} $display="flex" $fill>
    <Box $fill>
      <SearchGame
        setSearchFocused={setSearchFocused}
        searchFocused={searchFocused}
        mobileLandscape={true}
      />
    </Box>
    {!isMobileMenu && (
      <Box
        $display="grid"
        style={{ gridTemplateColumns: 'repeat(2, 62.5px)', gap: 8 }}
      >
        <Box>
          <SelectCategory />
        </Box>
        <Box>
          <SelectProvider searchFocused={searchFocused} />
        </Box>
      </Box>
    )}
  </Box>
);

export const DefaultLayout = ({
  isMobileMenu,
  mobileLandscape,
}: DefaultLayoutProps) => {
  const { user } = useUser();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  if (isMobileMenu && searchParams.size > 0) {
    router.replace(pathname, { scroll: false });
  }

  const shouldRenderDesktopElements = !isMobileMenu && !mobileLandscape;
  const isAuth = user?.isAuth;

  return (
    <Wrapper $alignToLeft={isMobileMenu}>
      {shouldRenderDesktopElements && (
        <>
          <SelectsWrapper>
            <Condition match={isAuth}>
              <SelectCategory />
            </Condition>
            <Condition match={!isAuth}>
              <SelectCategory />
            </Condition>
            <SelectProvider />
          </SelectsWrapper>
          <DeleteFilterWrapper>
            <DeleteFilter />
          </DeleteFilterWrapper>

          <SearchWrapper>
            <SearchGame mobileLandscape={mobileLandscape} />
          </SearchWrapper>
        </>
      )}
    </Wrapper>
  );
};

const GameFilter = ({ mobileLandscape, isMobileMenu, tablet }) => {
  const [searchFocused, setSearchFocused] = useState(false);
  const layoutType = tablet ? 'tablet' : 'default';
  const LayoutComponent = FilterConfig[layoutType].render;

  return (
    <LayoutComponent
      isMobileMenu={isMobileMenu}
      mobileLandscape={mobileLandscape}
      searchFocused={searchFocused}
      setSearchFocused={setSearchFocused}
    />
  );
};

const GameFilterWithBreakpointsSlice = withBreakpointsSlice(
  GameFilter,
  ({ mobileLandscape, tablet }) => ({ mobileLandscape, tablet })
);

export { GameFilterWithBreakpointsSlice as GameFilter };
